/**
 * URLSearchParams in-place-modifying helpers
 * Supply them with a copy of URLSearchParams instance.
 */

/**
 * Assigns a specified value to a given key.
 * If the value is an empty string, null, or undefined, the key is deleted.
 */
export function set(
  params: URLSearchParams,
  key: string,
  value?: string | null | undefined,
): URLSearchParams {
  if (value === "" || value === null || value === undefined) {
    params.delete(key)
    return params
  }
  params.set(key, value)
  return params
}

/**
 * Keeps only the values that are present in the values array.
 * Works for comma separated search param values.
 */
export function intersection(
  params: URLSearchParams,
  key: string,
  values: string[],
): URLSearchParams {
  const currentValues = params.get(key)
  if (currentValues === null) return params
  const currentValuesArray = currentValues.split(",")
  const newValues = currentValuesArray.filter((v) => values.includes(v))
  return set(params, key, newValues.join(","))
}

/**
 * Toggles a value in a comma separated array for a given key.
 */
export function toggleCommaSeparatedValue(
  params: URLSearchParams,
  key: string,
  value: string,
): URLSearchParams {
  const currentValue = params.get(key)
  // if the key does not exist, set it to the value
  if (currentValue === null) {
    params.set(key, value)
    return params
  }
  const currentValueArray = currentValue.split(",")

  // if the value is already in the array, remove it
  if (currentValueArray.includes(value)) {
    const newValue = currentValueArray.filter((v) => v !== value).join(",")
    return set(params, key, newValue)
  }

  // if the value is not in the array, add it
  const newValue = [...currentValueArray, value].join(",")
  return set(params, key, newValue)
}

/**
 * Removes a value from a comma separated array for a given key.
 */
export function removeCommaSeparatedValue(
  params: URLSearchParams,
  key: string,
  value: string,
): URLSearchParams {
  const currentValue = params.get(key)
  if (currentValue === null) return params
  const currentValueArray = currentValue.split(",")
  const newValue = currentValueArray.filter((v) => v !== value).join(",")
  return set(params, key, newValue)
}
